import { ref, watch, onMounted, onUnmounted } from 'vue'
import { isEditing, useEditingState } from '@/composables/useEditingState'
import { swNeedRefresh } from '@/composables/swUpdateSignal'

const CHECK_INTERVAL_MS = 5 * 60 * 1000 // 5 Minuten
// iOS-Standalone-PWAs feuern `visibilitychange`/`pageshow` beim Zurückkehren
// über den App-Switcher unzuverlässig; `focus` kommt dort am ehesten an. Wir
// hören zusätzlich darauf, drosseln den daraus ausgelösten version.json-Check
// aber, weil `focus` auch häufig feuert (z.B. beim Schließen der Tastatur).
const WAKE_RECHECK_THROTTLE_MS = 20 * 1000
const RELOAD_GUARD_WINDOW_MS = 30 * 1000
// Automatische Reloads pro Zielversion begrenzen: liefert das Deployment weiter
// eine alte Version aus, darf daraus nie eine endlose Reload-Schleife werden.
// Danach bleibt der manuelle "Jetzt aktualisieren"-Button verfügbar.
const MAX_AUTO_RELOAD_ATTEMPTS = 3
// Der Zähler verfällt: Ein installiertes PWA-Fenster lebt wochenlang und teilt
// sich denselben sessionStorage. Ohne Verfall bliebe es nach drei erfolglosen
// Versuchen dauerhaft auf einem alten Build stehen.
const AUTO_RELOAD_ATTEMPTS_RESET_MS = 10 * 60 * 1000
const RELOAD_TARGET_KEY = 'smarthome:reload-target-version'
const RELOAD_ATTEMPT_KEY = 'smarthome:reload-attempted-at'
const AUTO_RELOAD_ATTEMPTS_KEY = 'smarthome:auto-reload-attempts'

/**
 * Vergleichsschlüssel einer Auslieferung. Die Build-ID (Pipeline + Commit) ist
 * die eigentliche Wahrheit, weil `version.txt` nur vom lokalen pre-push-Hook
 * erhöht wird und ein Pipeline-Rerun sonst unter derselben Version ausgeliefert
 * würde. Sie wird nur benutzt, wenn beide Seiten eine haben – ein Client aus
 * der Zeit vor der Build-ID vergleicht weiterhin nur die Version.
 */
function releaseKey(version: string, buildId: string): string {
  return buildId ? `${version}@${buildId}` : version
}

/**
 * Dem wartenden Service Worker `SKIP_WAITING` schicken – reine Kür, ohne
 * Warten. Der Reload funktioniert auch ohne ihn: Die App-Shell wird nie aus
 * dem Precache beantwortet (siehe vite.pwa.ts), also liefert jede Navigation
 * den frischen Build. Das Signal sorgt nur dafür, dass der Precache der
 * gehashten Assets zeitnah nachzieht, statt bis zum Schließen aller Fenster
 * im `waiting`-Zustand zu verharren.
 */
function promoteWaitingWorker() {
  try {
    void navigator.serviceWorker?.getRegistration?.()
      .then((registration) => {
        const pendingWorker = registration?.waiting ?? registration?.installing
        pendingWorker?.postMessage({ type: 'SKIP_WAITING' })
      })
      .catch(() => {
        // Service-Worker-API nicht verfügbar oder blockiert – irrelevant.
      })
  } catch {
    // Ebenfalls irrelevant: Der Reload läuft unabhängig davon.
  }
}

export function useVersionCheck() {
  const updateAvailable = ref(false)
  const newVersion = ref('')
  // Automatik durch das Versuchslimit gestoppt – ab dann hilft nur noch der
  // manuelle Button, der Banner muss also sichtbar werden.
  const autoReloadBlocked = ref(false)
  let timer: ReturnType<typeof setInterval> | null = null
  let guardDelayTimer: ReturnType<typeof setTimeout> | null = null
  // Zeitpunkt des letzten version.json-Checks – drosselt den focus-Trigger, ohne
  // die (bewusst sofortigen) visibilitychange-/pageshow-Checks zu blockieren.
  let lastCheckAt = 0
  const currentVersion = __APP_VERSION__
  const currentBuildId = typeof __APP_BUILD_ID__ === 'string' ? __APP_BUILD_ID__ : ''
  // Ziel-Release des erkannten Updates (intern), während `newVersion` nur den
  // anzeigbaren Teil enthält.
  let newRelease = ''
  const versionChecksEnabled = import.meta.env.MODE !== 'development'

  if (!versionChecksEnabled) {
    return {
      updateAvailable,
      newVersion,
      currentVersion,
      autoReloadBlocked,
      isEditing,
      reload: () => {},
    }
  }

  useEditingState()

  const currentRelease = releaseKey(currentVersion, currentBuildId)

  /**
   * Ist das gespeicherte Reload-Ziel bereits erreicht? Der Vergleich lässt die
   * reine Version gelten, weil ein Ziel ohne Build-ID entstanden sein kann
   * (Server ohne Build-ID, siehe checkVersion) – sonst bliebe der Marker stehen
   * und löste Reload-Versuche auf eine längst laufende Version aus.
   */
  function landedOn(targetRelease: string) {
    return targetRelease === currentRelease || targetRelease === currentVersion
  }

  // We successfully landed on the release we were trying to reach – clear the
  // pending-reload markers so a future update starts from a clean slate.
  if (landedOn(sessionStorage.getItem(RELOAD_TARGET_KEY) ?? '')) {
    sessionStorage.removeItem(RELOAD_TARGET_KEY)
    sessionStorage.removeItem(RELOAD_ATTEMPT_KEY)
    sessionStorage.removeItem(AUTO_RELOAD_ATTEMPTS_KEY)
  }

  function getPendingReloadTarget() {
    return sessionStorage.getItem(RELOAD_TARGET_KEY)
  }

  function getLastReloadAttemptAt() {
    const lastAttemptRaw = sessionStorage.getItem(RELOAD_ATTEMPT_KEY)
    const lastAttemptAt = lastAttemptRaw ? Number(lastAttemptRaw) : 0

    return Number.isFinite(lastAttemptAt) ? lastAttemptAt : 0
  }

  function markReloadAttempt(targetRelease: string) {
    sessionStorage.setItem(RELOAD_TARGET_KEY, targetRelease)
    sessionStorage.setItem(RELOAD_ATTEMPT_KEY, String(Date.now()))
  }

  function getAutoReloadAttempts(targetRelease: string): number {
    try {
      const raw = sessionStorage.getItem(AUTO_RELOAD_ATTEMPTS_KEY)
      if (!raw) return 0
      const parsed = JSON.parse(raw) as { target?: string, count?: number, firstAt?: number }

      if (parsed.target !== targetRelease || typeof parsed.count !== 'number') return 0
      // Verfallen lassen, damit ein lange offenes Fenster sich selbst heilt.
      if (typeof parsed.firstAt === 'number' && Date.now() - parsed.firstAt > AUTO_RELOAD_ATTEMPTS_RESET_MS) {
        sessionStorage.removeItem(AUTO_RELOAD_ATTEMPTS_KEY)

        return 0
      }

      return parsed.count
    } catch {
      return 0
    }
  }

  function markAutoReloadAttempt(targetRelease: string) {
    const previous = getAutoReloadAttempts(targetRelease)
    let firstAt = Date.now()

    if (previous > 0) {
      try {
        const raw = sessionStorage.getItem(AUTO_RELOAD_ATTEMPTS_KEY)
        const parsed = raw ? JSON.parse(raw) as { firstAt?: number } : null
        if (typeof parsed?.firstAt === 'number') firstAt = parsed.firstAt
      } catch {
        // Defekter Eintrag – Fenster beginnt neu.
      }
    }

    sessionStorage.setItem(AUTO_RELOAD_ATTEMPTS_KEY, JSON.stringify({
      target: targetRelease,
      count: previous + 1,
      firstAt,
    }))
  }

  function cancelPendingReload() {
    if (guardDelayTimer) {
      clearTimeout(guardDelayTimer)
      guardDelayTimer = null
    }
  }

  /**
   * Seite neu laden. Das genügt, weil die App-Shell nie aus dem Precache
   * beantwortet wird: Die Navigation geht an den nginx, der `index.html` mit
   * `no-store` ausliefert, und die neuen gehashten Assets liegen ohnehin in
   * keinem Cache. Deshalb braucht es hier weder `controllerchange`-Rennen noch
   * das Löschen sämtlicher Caches (das früher auch API-, Bild- und Font-Cache
   * mitgerissen hat).
   */
  function triggerReload(targetRelease: string) {
    cancelPendingReload()
    markReloadAttempt(targetRelease)
    promoteWaitingWorker()
    window.location.reload()
  }

  /**
   * Kam der Tab zurück, ohne dass ein angestoßener Reload je durchlief (z.B.
   * im Hintergrund eingefroren), nach Ablauf des Guard-Fensters erneut
   * versuchen.
   */
  function retryPendingReload() {
    const pendingTarget = getPendingReloadTarget()

    if (!pendingTarget || landedOn(pendingTarget)) {
      return
    }

    if (Date.now() - getLastReloadAttemptAt() < RELOAD_GUARD_WINDOW_MS) {
      return
    }

    if (getAutoReloadAttempts(pendingTarget) >= MAX_AUTO_RELOAD_ATTEMPTS) {
      return
    }

    markAutoReloadAttempt(pendingTarget)
    triggerReload(pendingTarget)
  }

  /**
   * Sofort neu laden, sobald ein Update vorliegt und gerade nichts bearbeitet
   * wird. Während einer Bearbeitung wird gewartet; endet sie (z.B. nach dem
   * Speichern oder Schließen eines Dialogs), lädt die Seite unmittelbar.
   * Das Guard-Fenster und das Versuchslimit verhindern Reload-Schleifen, wenn
   * ein Deployment weiter die alte Version ausliefert.
   */
  function maybeReloadNow() {
    if (!updateAvailable.value) return
    if (guardDelayTimer) return
    if (isEditing.value) return

    const targetRelease = newRelease
    if (!targetRelease || landedOn(targetRelease)) return

    if (getAutoReloadAttempts(targetRelease) >= MAX_AUTO_RELOAD_ATTEMPTS) {
      autoReloadBlocked.value = true

      return
    }

    const sinceLastAttempt = Date.now() - getLastReloadAttemptAt()
    if (getPendingReloadTarget() === targetRelease && sinceLastAttempt < RELOAD_GUARD_WINDOW_MS) {
      guardDelayTimer = setTimeout(() => {
        guardDelayTimer = null
        maybeReloadNow()
      }, RELOAD_GUARD_WINDOW_MS - sinceLastAttempt)

      return
    }

    markAutoReloadAttempt(targetRelease)
    triggerReload(targetRelease)
  }

  watch([updateAvailable, isEditing], ([available, editing]) => {
    if (!available) return

    if (editing) {
      // Der Nutzer hat mit dem Bearbeiten begonnen – einen wartenden Reload
      // zurückstellen, bis die Bearbeitung abgeschlossen ist.
      cancelPendingReload()

      return
    }

    maybeReloadNow()
  })

  // Schnellpfad: pwa.ts meldet einen wartenden neuen Service Worker – sofort
  // autoritativ per version.json prüfen statt auf das nächste Poll-Intervall
  // zu warten. Das Signal löst selbst nie einen Reload aus.
  watch(swNeedRefresh, (needsRefresh) => {
    if (!needsRefresh) return
    swNeedRefresh.value = false
    void checkVersion()
  }, { immediate: true })

  async function checkVersion() {
    lastCheckAt = Date.now()
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
      })
      if (!res.ok) return
      const data = await res.json()
      if (!data.version) return

      const serverBuildId = typeof data.buildId === 'string' ? data.buildId : ''
      // Build-ID nur heranziehen, wenn beide Seiten eine haben – sonst würde
      // ein älteres Image ohne Build-ID (oder ein Client von davor) dauerhaft
      // als Unterschied gelten und eine Reload-Schleife auslösen.
      const useBuildId = Boolean(currentBuildId && serverBuildId)
      const serverRelease = useBuildId ? releaseKey(data.version, serverBuildId) : data.version
      const currentComparable = useBuildId ? currentRelease : currentVersion

      // Das Polling läuft nach einer Erkennung bewusst weiter: So gewinnt
      // immer die zuletzt deployte Version (Supersede) und ein Rollback auf
      // die laufende Version räumt das Banner wieder ab.
      if (serverRelease === currentComparable) {
        if (updateAvailable.value) {
          cancelPendingReload()
          updateAvailable.value = false
          autoReloadBlocked.value = false
          newVersion.value = ''
          newRelease = ''
        }

        return
      }

      if (newRelease && newRelease !== serverRelease) {
        // Neuere Version löst das bisherige Ziel ab – wartenden Reload
        // verwerfen, der Attempt-Zähler ist pro Ziel-Release und beginnt
        // damit ohnehin frisch.
        cancelPendingReload()
        autoReloadBlocked.value = false
      }

      newRelease = serverRelease
      newVersion.value = data.version
      updateAvailable.value = true
      maybeReloadNow()
    } catch {
      // Netzwerkfehler ignorieren – nächster Check kommt
    }
  }

  function start() {
    if (timer) return
    timer = setInterval(checkVersion, CHECK_INTERVAL_MS)
  }

  function stop() {
    if (timer) {
      clearInterval(timer)
      timer = null
    }
  }

  function onVisibility() {
    if (document.hidden) {
      stop()
      return
    }

    retryPendingReload()
    void checkVersion()
    start()
  }

  function onPageShow() {
    retryPendingReload()

    if (!document.hidden) {
      void checkVersion()
      start()
    }
  }

  /**
   * Aufwachen über das window-`focus`-Event. Auf iOS-Standalone ist das beim
   * Zurückkehren aus dem App-Switcher oft das einzige Event, das feuert –
   * `visibilitychange`/`pageshow` bleiben dort häufig aus. Der Check wird
   * gedrosselt, damit die auf iOS häufigen focus-Events version.json nicht
   * überfluten; das Retry-Fenster hat seinen eigenen Guard.
   */
  function onFocus() {
    retryPendingReload()

    if (document.hidden) {
      return
    }

    start()

    if (Date.now() - lastCheckAt >= WAKE_RECHECK_THROTTLE_MS) {
      void checkVersion()
    }
  }

  // Triggered by the user clicking "Jetzt aktualisieren" – must always reload,
  // bypassing both the guard window and the attempt cap.
  function reload() {
    triggerReload(newRelease || currentRelease)
  }

  onMounted(() => {
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pageshow', onPageShow)
    window.addEventListener('focus', onFocus)
    retryPendingReload()
    void checkVersion()
    start()
  })

  onUnmounted(() => {
    document.removeEventListener('visibilitychange', onVisibility)
    window.removeEventListener('pageshow', onPageShow)
    window.removeEventListener('focus', onFocus)
    stop()
    cancelPendingReload()
  })

  return { updateAvailable, newVersion, currentVersion, autoReloadBlocked, isEditing, reload }
}
